import { useQuery } from '@tanstack/react-query';
import { CalendarRange, TrendingUp } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';

type PAAItem = {
  mes: string;
  partida_codigo: string; partida_nombre: string;
  tipo_adjudicacion: string;
  total_requisiciones: number; total_ordenes: number;
  monto_requisitado: number; monto_ordenado: number;
};

type PAA = {
  items: PAAItem[];
  total_requisiciones: number; total_ordenes: number;
  monto_requisitado: number; monto_ordenado: number;
};

export function PAAPage() {
  const { data } = useQuery<PAA>({
    queryKey: ['paa-consolidado'],
    queryFn: () => api<PAA>('/paa/consolidado'),
  });

  if (!data) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  // Agrupar por mes para visualización
  const porMes = new Map<string, PAAItem[]>();
  for (const it of data.items) {
    if (!porMes.has(it.mes)) porMes.set(it.mes, []);
    porMes.get(it.mes)!.push(it);
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Adquisiciones · PAA</p>
        <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Programa Anual de Adquisiciones</h1>
        <p className="mt-1 text-[14px] text-ink-mute">
          Consolidado de lo requisitado y ordenado en el ejercicio, agrupado por mes, partida y modalidad.
        </p>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <KPI label="Requisiciones" value={data.total_requisiciones.toString()} icon={<CalendarRange size={16} />} />
        <KPI label="Órdenes de compra" value={data.total_ordenes.toString()} icon={<TrendingUp size={16} />} />
        <KPI label="Monto requisitado" value={fmt.money(data.monto_requisitado)} />
        <KPI label="Monto ordenado" value={fmt.money(data.monto_ordenado)} />
      </section>

      {[...porMes.entries()].map(([mes, items]) => {
        const totMes = items.reduce((a, b) => a + b.monto_ordenado, 0);
        return (
          <Card key={mes}>
            <CardHeader title={mes} subtitle={`${items.length} partidas · ${fmt.money(totMes)}`} />
            <CardBody className="p-0">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Partida COG</th>
                    <th className="px-5 py-3">Modalidad</th>
                    <th className="px-5 py-3 text-right">Reqs</th>
                    <th className="px-5 py-3 text-right">OCs</th>
                    <th className="px-5 py-3 text-right">Requisitado</th>
                    <th className="px-5 py-3 text-right">Ordenado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((it, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3">
                        <div className="num">{it.partida_codigo || '—'}</div>
                        <div className="text-[11px] text-ink-mute">{it.partida_nombre}</div>
                      </td>
                      <td className="px-5 py-3 capitalize">{it.tipo_adjudicacion.replace(/_/g, ' ')}</td>
                      <td className="num px-5 py-3 text-right">{it.total_requisiciones}</td>
                      <td className="num px-5 py-3 text-right">{it.total_ordenes}</td>
                      <td className="num px-5 py-3 text-right text-ink-mute">{fmt.money(it.monto_requisitado)}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.money(it.monto_ordenado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardBody>
          </Card>
        );
      })}

      {data.items.length === 0 && (
        <Card>
          <CardBody>
            <p className="text-center text-[13px] text-ink-mute">Sin actividad adquisitiva en el ejercicio.</p>
          </CardBody>
        </Card>
      )}
    </div>
  );
}

function KPI({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <div className="flex items-start justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
        {icon && <span className="text-ink-soft">{icon}</span>}
      </div>
      <div className="num mt-3 text-[22px] font-semibold text-ink">{value}</div>
    </div>
  );
}
